import { z } from 'zod';

/**
 * Guest Shipping Address Schema
 * Used by GuestAddressAutocomplete component
 * Note: Uses postal_code instead of zip (unlike AddressSchema)
 */
export const GuestShippingAddressSchema = z.object({
  name: z.string(),
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  postal_code: z.string(),
  country: z.string(),
});

export type GuestShippingAddress = z.infer<typeof GuestShippingAddressSchema>;

/**
 * GuestAddressAutocomplete Component Props Schema
 * Validates props passed to the GuestAddressAutocomplete component
 */
export const GuestAddressAutocompletePropsSchema = z.object({
  /** Guest's shipping address */
  shippingAddress: GuestShippingAddressSchema,

  /** Callback when address changes */
  onAddressChange: z.function(),

  /** Callback when address entry is complete */
  onAddressComplete: z.function().optional(),

  /** Optional validation errors */
  errors: z.any().optional(),

  /** Optional error setter */
  setErrors: z.function().optional(),

  /** Label for name field */
  nameLabel: z.string(),

  /** Label for address field */
  addressLabel: z.string(),

  /** Custom error message for invalid name */
  nameInvalidErrorMessage: z.string().optional(),

  /** Custom error message for missing last name */
  lastNameNeededErrorMessage: z.string().optional(),

  /** Custom error message shown when no address has been committed */
  addressMissingErrorMessage: z.string().optional(),

  /**
   * The email field, rendered between Name and Address.
   *
   * `z.any()` because it is a ReactNode -- an element, null, or nothing -- and
   * there is no useful shape to assert about it here. Its own contract is
   * FormInput's, checked where it is built.
   */
  emailField: z.any().optional(),
});

export type GuestAddressAutocompleteProps = z.infer<typeof GuestAddressAutocompletePropsSchema>;

/**
 * Minimal props schema for simpler test scenarios
 * Only requires the essential props
 */
export const GuestAddressAutocompleteMinimalPropsSchema = z.object({
  shippingAddress: GuestShippingAddressSchema,
  onAddressChange: z.function(),
  onAddressComplete: z.function().optional(),
});

export type GuestAddressAutocompleteMinimalProps = z.infer<typeof GuestAddressAutocompleteMinimalPropsSchema>;
